import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { blogApi, type BlogPostDetail } from '../lib/blogApi';
import { SiteHeader } from '../components/SiteHeader';
import { SEO } from '../components/SEO';

/** Single blog post detail page. */
export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPostDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    blogApi
      .get(slug)
      .then(setPost)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {post && (
        <SEO
          title={`${post.title} — Dormant Accounts Filing`}
          description={post.excerpt || `${post.title} — guidance from Dormant Accounts Filing.`}
          path={`/blog/${post.slug}`}
        />
      )}
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
        <Link to="/blog" className="text-sm text-brand-700 hover:underline">
          ← All posts
        </Link>

        {loading && <p className="mt-10 text-sm text-slate-500">Loading…</p>}
        {error && (
          <div className="mt-10 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        {post && (
          <article>
            {post.coverImageUrl && (
              // object-contain so illustrations / portrait images don't get
              // cropped; max-h keeps photos from dominating the screen.
              // The bg colour fills any letterboxing so it doesn't look
              // empty for images that don't fill the box.
              <img
                src={post.coverImageUrl}
                alt=""
                className="mt-6 max-h-[28rem] w-full rounded-xl bg-slate-100 object-contain ring-1 ring-slate-200"
              />
            )}
            <div className="mt-6 text-xs text-slate-500">
              {formatDate(post.publishedAt)} · {post.author}
            </div>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="mt-4 text-lg leading-relaxed text-slate-600">{post.excerpt}</p>
            )}

            {/* Rendered Markdown – already sanitised on the server. */}
            <div
              className="blog-content mt-8 text-slate-800"
              dangerouslySetInnerHTML={{ __html: post.html }}
            />

            <div className="mt-12 rounded-xl bg-brand-50 p-6 text-center ring-1 ring-brand-200">
              <h3 className="text-lg font-semibold text-slate-900">Ready to file?</h3>
              <p className="mt-1 text-sm text-slate-600">
                File your dormant company accounts directly with Companies House for £20.
              </p>
              <Link to="/search" className="btn-primary mt-4 inline-flex">
                File now →
              </Link>
            </div>
          </article>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-slate-900 py-10 text-slate-400">
        <div className="mx-auto max-w-6xl px-6 text-xs">
          © {new Date().getFullYear()} Dormant Accounts Filing.{' '}
          <Link to="/privacy" className="hover:text-white">Privacy</Link> ·{' '}
          <Link to="/terms" className="hover:text-white">Terms</Link> ·{' '}
          <Link to="/contact" className="hover:text-white">Contact</Link>
        </div>
      </footer>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(+d)) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
